import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Store,
  Package,
  ShoppingCart,
  BarChart3,
  Link2,
  Settings,
  LogOut,
  Eye,
  TrendingUp,
  DollarSign,
  Users,
  Palette,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import ProductManagement from "@/components/Weaver/ProductManagement";
import WeaverAnalytics from "@/components/Weaver/WeaverAnalytics";
import WeaverOrders from "@/components/Weaver/WeaverOrders";
import StoreCustomizer from "@/components/Weaver/StoreCustomizer";
import LinkGenerator from "@/components/Weaver/LinkGenerator";

interface WeaverStats {
  store_id: string;
  store_name: string;
  total_products: number;
  total_orders: number;
  pending_orders: number;
  completed_orders: number;
  total_revenue: number;
  total_views: number;
  views_this_week: number;
  views_this_month: number;
}

const WeaverDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WeaverStats | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  useEffect(() => {
    checkWeaverAccess();
  }, []);

  const checkWeaverAccess = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please login to access weaver dashboard");
        navigate("/login");
        return;
      }

      // Check if user has weaver role
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "weaver")
        .single();

      if (roleError || !roleData) {
        toast.error("Access denied. Weaver privileges required.");
        navigate("/");
        return;
      }

      // Load weaver analytics
      await loadDashboardData(user.id);
    } catch (error) {
      console.error("Error checking weaver access:", error);
      navigate("/login");
    }
  };

  const loadDashboardData = async (userId: string) => {
    setLoading(true);
    try {
      // Get weaver analytics using the database function
      const { data, error } = await supabase.rpc("get_weaver_analytics", {
        weaver_user_id: userId,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setStats(data[0]);
      }
    } catch (error: any) {
      console.error("Error loading dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out successfully");
    navigate("/");
  };

  const refreshData = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      await loadDashboardData(user.id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-lg text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-primary to-secondary rounded-lg">
                <Store className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">
                  {stats?.store_name || "Weaver Dashboard"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  Manage your store and track performance
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={refreshData}>
                <TrendingUp className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Products
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.total_products || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Active in your store
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Orders
              </CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.total_orders || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.pending_orders || 0} pending
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Revenue
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                GH₵{Number(stats?.total_revenue || 0).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.completed_orders || 0} completed orders
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Store Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats?.total_views || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                +{stats?.views_this_week || 0} this week
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6">
            <TabsTrigger value="overview">
              <Store className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Products</span>
            </TabsTrigger>
            <TabsTrigger value="orders">
              <ShoppingCart className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Orders</span>
            </TabsTrigger>
            <TabsTrigger value="analytics">
              <BarChart3 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="customize">
              <Palette className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Customize</span>
            </TabsTrigger>
            <TabsTrigger value="links">
              <Link2 className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Links</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                  <CardDescription>
                    Common tasks to manage your store
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("products")}
                  >
                    <Package className="mr-2 h-4 w-4" />
                    Add New Product
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("orders")}
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    View Pending Orders
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("customize")}
                  >
                    <Palette className="mr-2 h-4 w-4" />
                    Customize Store
                  </Button>
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    onClick={() => setActiveTab("links")}
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    Generate Share Link
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Latest updates to your store
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Users className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Store Views</p>
                        <p className="text-xs text-muted-foreground">
                          +{stats?.views_this_week || 0} views this week
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ShoppingCart className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Orders</p>
                        <p className="text-xs text-muted-foreground">
                          {stats?.pending_orders || 0} orders pending
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <DollarSign className="h-4 w-4 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Revenue</p>
                        <p className="text-xs text-muted-foreground">
                          GH₵{Number(stats?.total_revenue || 0).toFixed(2)}{" "}
                          total
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ProductManagement
              storeId={stats?.store_id || ""}
              onUpdate={refreshData}
            />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <WeaverOrders onUpdate={refreshData} />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <WeaverAnalytics storeId={stats?.store_id || ""} />
          </TabsContent>

          {/* Customize Tab */}
          <TabsContent value="customize">
            <StoreCustomizer
              storeId={stats?.store_id || ""}
              onUpdate={refreshData}
            />
          </TabsContent>

          {/* Links Tab */}
          <TabsContent value="links">
            <LinkGenerator storeId={stats?.store_id || ""} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default WeaverDashboard;
